import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	useReactTable,
	type ColumnFiltersState,
	type SortingState,
} from '@tanstack/react-table';
import { Edit, Folder, Trash2 } from 'lucide-react';
import { memo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { useFoldersPaginated } from '../../hooks/useFolders';
import type { Folder as FolderType } from '../../types/api';
import type { FolderPaginationParams } from '../../types/pagination';
import { PaginationControls } from '../shared/PaginationControls';
import { SearchInput } from '../shared/SearchInput';

/**
 * Props for the FoldersTable component
 */
interface FoldersTableProps {
	initialFilters?: Partial<FolderPaginationParams>;
	showSearch?: boolean;
	className?: string;
	onEdit?: (folder: FolderType) => void;
	onDelete?: (folder: FolderType) => void;
	onFolderSelect?: (folder: FolderType) => void;
}

/**
 * Column helper for type-safe column definitions
 */
const columnHelper = createColumnHelper<FolderType>();

/**
 * Performant folders table with server-side pagination, sorting, and search
 */
export function FoldersTable({
	initialFilters = {},
	showSearch = true,
	className = '',
	onEdit,
	onDelete,
	onFolderSelect,
}: FoldersTableProps) {
	// React Query pagination hook
	const [params, setParams] = useState<FolderPaginationParams>({
		page: 1,
		limit: 20,
		sortBy: 'name',
		sortOrder: 'ASC',
		...initialFilters,
	});

	const { data: foldersResponse, isLoading: loading, error } = useFoldersPaginated(params);

	// Extract data and pagination from response
	const folders = foldersResponse?.data || [];
	const pagination = foldersResponse?.pagination;

	// Table state
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

	// Handle server-side sorting
	const handleSortingChange = (updater: any) => {
		const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
		setSorting(newSorting);

		if (newSorting.length > 0) {
			const sort = newSorting[0];
			setParams(prev => ({
				...prev,
				sortBy: sort.id,
				sortOrder: sort.desc ? 'DESC' : 'ASC',
				page: 1, // Reset to first page when sorting changes
			}));
		}
	};

	// Handle pagination
	const goToPage = (page: number) => {
		setParams(prev => ({ ...prev, page }));
	};

	const goToNextPage = () => {
		if (pagination?.hasNext) {
			goToPage(pagination.page + 1);
		}
	};

	const goToPreviousPage = () => {
		if (pagination?.hasPrevious) {
			goToPage(pagination.page - 1);
		}
	};

	const changeLimit = (limit: number) => {
		setParams(prev => ({ ...prev, limit, page: 1 }));
	};

	// Handle search
	const search = (searchTerm: string) => {
		setParams(prev => ({ ...prev, search: searchTerm, page: 1 }));
	};

	// Handle filter clearing
	const clearFilters = () => {
		setParams({
			page: 1,
			limit: 20,
			sortBy: 'name',
			sortOrder: 'ASC',
		});
	};

	// Column definitions
	const columns = [
		columnHelper.accessor('name', {
			header: 'Name',
			cell: info => (
				<div className="flex gap-3 items-center">
					<div className="flex-shrink-0">
						<Folder
							className="w-5 h-5"
							style={{ color: info.row.original.color || '#3B82F6' }}
						/>
					</div>
					<div>
						<div className="font-medium text-gray-900 dark:text-white">
							{info.getValue()}
						</div>
						{info.row.original.description && (
							<div className="text-sm text-gray-500 dark:text-gray-400 truncate">
								{info.row.original.description}
							</div>
						)}
					</div>
				</div>
			),
		}),
		columnHelper.accessor('fullPath', {
			header: 'Folder Path',
			cell: info => (
				<div className="text-sm">
					<span
						className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
						title="Click to navigate to this folder"
						onClick={() => onFolderSelect?.(info.row.original)}
					>
						{info.getValue() || 'Root'}
					</span>
					<div className="text-xs text-gray-400 dark:text-gray-500">
						Depth: {info.row.original.depth}
					</div>
				</div>
			),
		}),
		columnHelper.accessor('flowCount', {
			header: 'Workflows',
			cell: info => (
				<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
					{info.getValue()} workflow{info.getValue() !== 1 ? 's' : ''}
				</span>
			),
		}),
		columnHelper.accessor('createdAt', {
			header: 'Created',
			cell: info => {
				const date = new Date(info.getValue());
				return (
					<div className="text-sm text-gray-500 dark:text-gray-400">
						{date.toLocaleDateString()}
					</div>
				);
			},
		}),
		columnHelper.display({
			id: 'actions',
			header: 'Actions',
			cell: ({ row }) => (
				<div className="flex gap-2 items-center" onClick={e => e.stopPropagation()}>
					{onEdit && (
						<button
							onClick={() => onEdit(row.original)}
							className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
							data-tooltip-id={`edit-folder-${row.original.id}`}
							data-tooltip-content="Edit folder"
						>
							<Edit className="w-4 h-4" />
						</button>
					)}
					{onDelete && (
						<button
							onClick={() => onDelete(row.original)}
							className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
							data-tooltip-id={`delete-folder-${row.original.id}`}
							data-tooltip-content="Delete folder"
						>
							<Trash2 className="w-4 h-4" />
						</button>
					)}

					{/* Tooltips */}
					<Tooltip id={`edit-folder-${row.original.id}`} />
					<Tooltip id={`delete-folder-${row.original.id}`} />
				</div>
			),
		}),
	];

	// Create table instance
	const table = useReactTable({
		data: folders,
		columns,
		state: {
			sorting,
			columnFilters,
		},
		onSortingChange: handleSortingChange,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		manualPagination: true,
		manualSorting: true,
	});

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<div className="text-red-600 dark:text-red-400 mb-4">
					Error loading folders: {error.message}
				</div>
				<button
					onClick={() => window.location.reload()}
					className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
				>
					Retry
				</button>
			</div>
		);
	}

	return (
		<div className={`w-full space-y-6 ${className}`}>
			{/* Search */}
			{showSearch && (
				<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
					<SearchInput
						onSearch={search}
						placeholder="Search folders..."
						className="w-full sm:w-auto sm:min-w-[300px]"
					/>
				</div>
			)}

			{/* Loading State */}
			{loading && folders.length === 0 && (
				<div className="flex items-center justify-center py-12">
					<div className="flex items-center gap-2">
						<div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
						<span className="text-gray-600 dark:text-gray-400">Loading folders...</span>
					</div>
				</div>
			)}

			{/* Table */}
			<div className="w-full overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
				<table className="w-full min-w-full divide-y divide-gray-200 dark:divide-gray-700">
					<thead className="bg-gray-50 dark:bg-gray-800">
						{table.getHeaderGroups().map(headerGroup => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map(header => (
									<th
										key={header.id}
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext()
											  )}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
						{table.getRowModel().rows.map(row => (
							<tr
								key={row.id}
								className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
									onFolderSelect ? 'cursor-pointer' : ''
								}`}
								onDoubleClick={() => {
									// Navigate into folder on double-click
									onFolderSelect?.(row.original);
								}}
								title={
									onFolderSelect
										? `Double-click to open "${row.original.name}" folder`
										: undefined
								}
							>
								{row.getVisibleCells().map(cell => (
									<td key={cell.id} className="px-6 py-4 whitespace-nowrap">
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* No Results */}
			{!loading && folders.length === 0 && (
				<div className="text-center py-12">
					<div className="text-gray-500 dark:text-gray-400 mb-4">
						No folders found matching your criteria
					</div>
					<button
						onClick={clearFilters}
						className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
					>
						Clear all filters
					</button>
				</div>
			)}

			{/* Pagination Controls */}
			{pagination && (
				<PaginationControls
					pagination={pagination}
					onPageChange={goToPage}
					onNextPage={goToNextPage}
					onPreviousPage={goToPreviousPage}
					loading={loading}
					pageSizeOptions={[10, 20, 50]}
					onPageSizeChange={changeLimit}
					showPageSizeSelector={true}
				/>
			)}

			{/* Loading indicator for page changes */}
			{loading && folders.length > 0 && (
				<div className="fixed top-0 left-0 right-0 bg-blue-100 dark:bg-blue-900 border-b border-blue-200 dark:border-blue-800 py-2 z-50">
					<div className="flex items-center justify-center gap-2">
						<div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
						<span className="text-sm text-blue-800 dark:text-blue-200">Loading...</span>
					</div>
				</div>
			)}
		</div>
	);
}

// Memoize the component to prevent unnecessary re-renders
export default memo(FoldersTable);
